import { getSiteUiCopy } from "@/lib/site-ui-copy";
/**
 * Resources mega menu — one column per footer resource (excluding privacy).
 * Copy: `src/data/nav-resources-mega.json`. Column titles and parent hrefs from `getSiteNavResourceItems()`.
 * Blog sub-links come from `src/data/blog/lib/categories.json`.
 */
import categoriesJson from "@/data/blog/lib/categories.json";
import navResourcesMegaData from "@/data/nav-resources-mega.json";
import {
  buildResourceItemHref,
  getSiteNavResourceItems,
  type NavResourceKey,
} from "@/lib/cc-nav-resources";
import { pickBlogLocalized, type BlogCategory } from "@/lib/blog-cards";
import { getBlogCategoryLabel } from "@/lib/blog/category-label";
import { t } from "@/lib/content";
import { buildBlogHref } from "@/lib/i18n/paths";
import { toSiteLocale } from "@/lib/i18n/config";
import type { Lang } from "@/lib/types";

type LocalizedString = Record<string, string>;

type MegaLinkDef = {
  key: string;
  title: LocalizedString;
  description: LocalizedString;
};

type MegaColumnDef = {
  links: MegaLinkDef[];
};

type NavResourcesMegaData = {
  kicker: LocalizedString;
  seeAll: LocalizedString;
  viewResource: LocalizedString;
  columns: Record<string, MegaColumnDef>;
};

const megaData = navResourcesMegaData as NavResourcesMegaData;

/** Footer-aligned resource columns (privacy stays in footer only). */
export const NAV_RESOURCES_MEGA_KEYS = [
  "blog",
  "compare",
  "industries",
] as const satisfies readonly NavResourceKey[];

export type NavResourcesMegaColumnKey = (typeof NAV_RESOURCES_MEGA_KEYS)[number];

/** Max blog category sub-links shown in the resources mega menu. */
export const NAV_RESOURCES_MEGA_BLOG_CATEGORY_MAX = 4;

export type NavResourceMegaLink = {
  key: string;
  label: string;
  description: string;
  href: string;
};

export type NavResourceMegaColumn = {
  key: NavResourceKey;
  id: string;
  header: string;
  headerHref: string;
  viewLabel: string;
  links: NavResourceMegaLink[];
};

export type NavResourcesMegaCopy = {
  kicker: string;
  seeAll: string;
};

function buildResourceSubpageHref(
  locale: ReturnType<typeof toSiteLocale>,
  parentKey: NavResourceKey,
  subKey: string,
): string {
  const base = buildResourceItemHref(parentKey, locale);
  return `${base}/${subKey}`;
}

function buildBlogCategoryHref(
  locale: ReturnType<typeof toSiteLocale>,
  categorySlug: string,
): string {
  const base = buildBlogHref(locale);
  return `${base}/${encodeURIComponent(categorySlug)}`;
}

function blogCategoryLinks(lang: Lang, locale: ReturnType<typeof toSiteLocale>): NavResourceMegaLink[] {
  const categories = categoriesJson as BlogCategory[];
  const browseHint = getSiteUiCopy(lang).navigation.browseCategory;

  return categories
    .map((category) => {
      const slug =
        typeof category.slug === "string"
          ? category.slug
          : pickBlogLocalized(category.slug, lang);
      if (!slug) {
        return null;
      }
      const label = getBlogCategoryLabel(category, lang) || slug;
      return {
        key: slug,
        label,
        description: browseHint,
        href: buildBlogCategoryHref(locale, slug),
      };
    })
    .filter((link): link is NavResourceMegaLink => link !== null)
    .slice(0, NAV_RESOURCES_MEGA_BLOG_CATEGORY_MAX);
}

function columnLinksForResource(
  resourceKey: NavResourceKey,
  lang: Lang,
  locale: ReturnType<typeof toSiteLocale>,
): NavResourceMegaLink[] {
  if (resourceKey === "blog") {
    return blogCategoryLinks(lang, locale);
  }

  const columnDef = megaData.columns[resourceKey];
  if (!columnDef) {
    return [];
  }

  return columnDef.links.map((link) => ({
    key: link.key,
    label: t(link.title, lang),
    description: t(link.description, lang),
    href: buildResourceSubpageHref(locale, resourceKey, link.key),
  }));
}

export function getNavResourcesMegaCopy(lang: Lang): NavResourcesMegaCopy {
  return {
    kicker: t(megaData.kicker, lang),
    seeAll: t(megaData.seeAll, lang),
  };
}

/** Industries / Branscher column (resources mega + site footer second row). */
export function getNavResourcesIndustriesMegaColumn(
  lang: Lang,
): NavResourceMegaColumn | null {
  const column = getNavResourcesMegaColumns(lang).find(
    (item) => item.key === "industries",
  );
  return column ?? null;
}

/** One column per footer resource (excluding privacy), blog uses category sub-links. */
export function getNavResourcesMegaColumns(lang: Lang): NavResourceMegaColumn[] {
  const locale = toSiteLocale(lang);
  const megaKeys = new Set<string>(NAV_RESOURCES_MEGA_KEYS);
  const navItems = getSiteNavResourceItems(lang).filter((item) => megaKeys.has(item.key));

  return navItems.map((item) => ({
    key: item.key,
    id: item.key,
    header: item.label,
    headerHref: item.href,
    viewLabel: t(megaData.viewResource, lang).replace("{name}", item.label),
    links: columnLinksForResource(item.key, lang, locale),
  }));
}

/** Flat list of mega links (for active-state checks). */
export function flattenNavResourceMegaLinks(
  columns: NavResourceMegaColumn[],
): NavResourceMegaLink[] {
  return columns.flatMap((column) => column.links);
}

/** Column parent + sub-page hrefs (tests / discovery). */
export function flattenNavResourceMegaAllLinks(
  columns: NavResourceMegaColumn[],
): { label: string; href: string }[] {
  return columns.flatMap((column) => [
    { label: column.header, href: column.headerHref },
    ...column.links.map((link) => ({ label: link.label, href: link.href })),
  ]);
}
